import { useCallback, useEffect, useRef, useState } from 'react';

interface RenovationItemInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suggestions: readonly string[];
  className?: string;
  ariaLabel?: string;
}

export function RenovationItemInput({
  value,
  onChange,
  placeholder = 'e.g., New Kitchen Cabinets',
  suggestions,
  className = '',
  ariaLabel,
}: RenovationItemInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateFiltered = useCallback(
    (text: string) => {
      if (!text.trim()) {
        setFilteredSuggestions([...suggestions]);
        return;
      }
      const lower = text.toLowerCase();
      setFilteredSuggestions(suggestions.filter((s) => s.toLowerCase().includes(lower)));
    },
    [suggestions]
  );

  const handleFocus = useCallback(() => {
    updateFiltered(value);
    setIsOpen(true);
  }, [value, updateFiltered]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      onChange(text);
      updateFiltered(text);
      setIsOpen(true);
    },
    [onChange, updateFiltered]
  );

  const handleSelect = useCallback(
    (suggestion: string) => {
      onChange(suggestion);
      setIsOpen(false);
    },
    [onChange]
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        className={
          className ||
          'h-[36px] w-full rounded-[6px] border border-[#e5e5e5] bg-white px-[10px] text-[13px] focus:border-[#055135] focus:outline-none'
        }
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      {isOpen && filteredSuggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-[4px] max-h-[200px] overflow-y-auto rounded-[8px] border border-[#e5e5e5] bg-white shadow-lg">
          {filteredSuggestions.map((suggestion) => (
            <li key={suggestion}>
              <button
                type="button"
                onMouseDown={() => handleSelect(suggestion)}
                className="w-full px-[10px] py-[8px] text-left text-[13px] text-[#181818] hover:bg-[#f0faf6] transition-colors"
              >
                <span className="inline-block rounded-full bg-[#e2f2ef] px-[10px] py-[2px] text-[12px] text-[#055135]">
                  {suggestion}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
